import { useState } from 'react';
import { Plus } from 'lucide-react';
import { AbschnittRahmen, GesperrtesFeld, ZeilenKopf } from './listenTeile';
import { useWege, useWegAendern, useWegAnlegen } from '@/hooks/useWegeVerwaltung';
import type { WegArt, WegVerwaltung } from '@/hooks/useWegeVerwaltung';
import { BESTELLWEG_ICONS, bestellwegIcon } from '@/lib/bestellwegIcons';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { FELD_KLASSE } from '@/lib/feldKlassen';
import { useToast } from '@/lib/toast';
import { ApiError } from '@/lib/api';

/**
 * Bestellwege und Beschaffungswege pflegen (A40).
 *
 * DASSELBE MUSTER WIE DIE MITARBEITER (A36): Überschrift, ein Satz,
 * darunter je Eintrag eine Zeile, die sich zum Formular aufklappt.
 * Gespeichert wird je Eintrag.
 *
 * EINE KOMPONENTE FÜR BEIDE LISTEN, denn sie unterscheiden sich in
 * einer Spalte (`icon` gibt es nur beim Bestellweg) und in nichts
 * sonst. Zwei Kopien wären beim nächsten Umbau auseinandergelaufen —
 * derselbe Grund, aus dem die Sinnbild-Zuordnung jetzt an einer Stelle
 * steht (Fallstrick 15).
 *
 * HIER WIRD ANGELEGT, ABER NICHT GELÖSCHT
 *   Angelegt: ein Bestellweg entsteht nirgendwo sonst.
 *   Nicht gelöscht: an jedem Eintrag hängen Vorgänge, und wer ihn
 *   löscht, nimmt ihnen ihre Herkunft. Stattdessen „stilllegen": der
 *   Weg verschwindet aus der Auswahl beim Erfassen, bleibt aber an den
 *   alten Vorgängen stehen. Wie viele das sind, steht in der Zeile —
 *   das ist die Antwort auf die Frage, warum kein Löschen da ist.
 */

type Sorte = {
  titel: string;
  einleitung: string;
  einzahl: string;
  /** Wofür die Verwendungszahl zählt. */
  vorgang: (n: number) => string;
  /** Nur Bestellwege tragen ein Sinnbild. */
  mitIcon: boolean;
};

const SORTEN: Record<WegArt, Sorte> = {
  bestellwege: {
    titel: 'Bestellwege',
    einleitung:
      'Woher eine Bestellung kommt — Telefon, Laden, Shop. Der Weg steht an jeder Bestellung, ' +
      'deshalb wird hier nichts gelöscht, sondern stillgelegt.',
    einzahl: 'Bestellweg',
    vorgang: (n) => (n === 1 ? '1 Bestellung' : `${n} Bestellungen`),
    mitIcon: true
  },
  beschaffungswege: {
    titel: 'Beschaffungswege',
    einleitung:
      'Woher ein Titel besorgt wird — Barsortiment, Verlag, eigenes Lager. Der Weg steht an ' +
      'jeder Beschaffung, deshalb wird hier nichts gelöscht, sondern stillgelegt.',
    einzahl: 'Beschaffungsweg',
    vorgang: (n) => (n === 1 ? '1 Beschaffung' : `${n} Beschaffungen`),
    mitIcon: false
  }
};

/** Was der Server an Ablehnungen schickt, in einem Satz. */
function fehlerText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : '';
  if (code === 'code_belegt') return 'Diesen Schlüssel gibt es schon.';
  if (code === 'code_ungueltig')
    return 'Der Schlüssel darf nur Kleinbuchstaben, Ziffern und Unterstriche enthalten.';
  if (code === 'feld_gesperrt') return 'Dieses Feld lässt sich nicht ändern.';
  if (code === 'ungueltig') return 'Bitte die Eingaben prüfen.';
  return 'Speichern fehlgeschlagen.';
}

export function WegeAbschnitt({ art }: { art: WegArt }) {
  const sorte = SORTEN[art];
  const { data, isLoading, isError } = useWege(art);
  const [neuOffen, setNeuOffen] = useState(false);

  return (
    <AbschnittRahmen
      ankerId={`einst-${art}`}
      titel={sorte.titel}
      einleitung={sorte.einleitung}
      laedt={isLoading}
      fehler={isError}
    >
      {/* `?? []` und nicht `data.wege.map`: Antwortet der Server ohne
          das Feld, bliebe der Abschnitt leer — statt die ganze
          Einstellungsseite weiß zu lassen (die Lehre aus A31). */}
      {data !== undefined && (
        <ul className="space-y-2">
          {(data.wege ?? []).map((w) => (
            <li key={w.id}>
              <WegZeile weg={w} art={art} sorte={sorte} />
            </li>
          ))}
        </ul>
      )}

      {neuOffen ? (
        <NeuerWeg art={art} sorte={sorte} onFertig={() => setNeuOffen(false)} />
      ) : (
        <Button variant="ghost" onClick={() => setNeuOffen(true)}>
          <Plus aria-hidden className="h-4 w-4" />
          {sorte.einzahl} hinzufügen
        </Button>
      )}
    </AbschnittRahmen>
  );
}

function WegZeile({ weg, art, sorte }: { weg: WegVerwaltung; art: WegArt; sorte: Sorte }) {
  const [offen, setOffen] = useState(false);
  const [label, setLabel] = useState(weg.label);
  const [icon, setIcon] = useState(weg.icon);
  const [aktiv, setAktiv] = useState(weg.aktiv);
  const [reihe, setReihe] = useState(String(weg.sort_order));
  const aendern = useWegAendern(art);
  const toast = useToast();

  const Icon = bestellwegIcon(weg.icon);

  function zuruecksetzen(): void {
    setLabel(weg.label);
    setIcon(weg.icon);
    setAktiv(weg.aktiv);
    setReihe(String(weg.sort_order));
    setOffen(false);
  }

  function speichern(): void {
    aendern.mutate(
      {
        id: weg.id,
        label: label.trim(),
        aktiv,
        sort_order: Number(reihe) || 0,
        ...(sorte.mitIcon ? { icon } : {})
      },
      {
        onSuccess: (d) => {
          toast(
            'success',
            d.geaendert.length === 0 ? 'Nichts zu ändern.' : `Gespeichert: ${d.weg.label}.`
          );
          setOffen(false);
        },
        onError: (e) => toast('error', fehlerText(e))
      }
    );
  }

  return (
    <div className="rounded-md border border-line bg-cream">
      {/* Gestalt und Kennzeichnung stehen in listenTeile.tsx — dieselbe
          Kopfzeile wie im Listen-Abschnitt. Genau diese Stelle musste
          A54 (#146) ein zweites Mal richten, nachdem A43 (#120) sie
          nebenan schon gerichtet hatte. Hier kommt nur herein, worin
          sich die beiden unterscheiden: vorn das Sinnbild, unten der
          Schlüssel mit der Verwendungszahl. */}
      <ZeilenKopf
        offen={offen}
        onUmschalten={() => setOffen((o) => !o)}
        label={weg.label}
        aktiv={weg.aktiv}
        vorne={
          sorte.mitIcon ? (
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line text-ink-soft">
              <Icon aria-hidden className="h-4 w-4" />
            </span>
          ) : undefined
        }
        unterzeile={
          <span className="block truncate text-xs text-ink-soft">
            <span className="font-mono">{weg.code}</span> · {sorte.vorgang(weg.verwendungen)}
          </span>
        }
      />

      {offen && (
        <div className="space-y-3 border-t border-line px-3 py-3">
          <label className="block text-xs text-ink-soft">
            Beschriftung
            <input
              type="text"
              value={label}
              maxLength={100}
              onChange={(e) => setLabel(e.target.value)}
              className={`mt-1 ${FELD_KLASSE}`}
            />
            <span className="mt-1 block text-ink-faint">
              Das ist der Text, der überall am Bildschirm steht. Ihn zu ändern ist
              gefahrlos.
            </span>
          </label>

          {sorte.mitIcon && <IconWahl wert={icon} onWahl={setIcon} />}

          <label className="block text-xs text-ink-soft">
            Reihenfolge
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={9999}
              value={reihe}
              onChange={(e) => setReihe(e.target.value)}
              className={`mt-1 ${FELD_KLASSE} tabular-nums`}
            />
            <span className="mt-1 block text-ink-faint">
              Kleinere Zahlen stehen vorn — in dieser Liste und in der Auswahl beim
              Erfassen.
            </span>
          </label>

          {/* Stilllegen statt löschen. Das Kästchen ist positiv
              formuliert („wird angeboten"), damit niemand einen Haken
              setzen muss, um etwas zu behalten. */}
          <label className="flex min-h-[44px] items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={aktiv}
              onChange={(e) => setAktiv(e.target.checked)}
              // `shrink-0` ist hier kein Zierrat: Die Zeile ist ein
              // Flex-Kasten, und ohne diese Klasse gibt das Kästchen
              // nach, sobald der Text daneben mehr Platz will. Gemessen
              // im gebauten Stand: 13 statt 20 Pixel breit — bei 390
              // Pixeln wie bei 1280, also kein Telefon-Fall, aber
              // derselbe Griff. Der Zwilling im Listen-Abschnitt trägt
              // die Klasse längst.
              className="h-5 w-5 shrink-0"
            />
            <span>
              Beim Erfassen zur Auswahl anbieten
              <span className="block text-xs text-ink-soft">
                Ohne Haken bleibt der {sorte.einzahl} an den{' '}
                {sorte.vorgang(weg.verwendungen)}, die ihn schon tragen — er wird nur
                nicht mehr angeboten. Gelöscht wird hier nichts.
              </span>
            </span>
          </label>

          {/* Der Schlüssel steht da, aber gesperrt. */}
          <GesperrtesFeld
            titel="Schlüssel"
            wert={weg.code}
            mono
            hilfe={
              <>
                Lässt sich nicht ändern: Einzelne Schlüssel stehen im Programm — der
                Shop-Import sucht „shopify", das Lager gilt als Weg ohne Bestellung.
                Eine Umbenennung bräche das, ohne eine Meldung zu erzeugen.
              </>
            }
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={speichern} disabled={aendern.isPending}>
              {aendern.isPending ? <Spinner /> : null}
              Speichern
            </Button>
            <Button variant="ghost" onClick={zuruecksetzen}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Ein neuer Weg.
 *
 * Der Schlüssel wird hier EINMAL vergeben und ist danach unveränderlich.
 * Er ist kein Anzeigetext: Er wird nur klein und einsilbig gebraucht,
 * und ein Vorschlag aus der Beschriftung erspart die meiste Tipparbeit.
 */
function NeuerWeg({
  art,
  sorte,
  onFertig
}: {
  art: WegArt;
  sorte: Sorte;
  onFertig: () => void;
}) {
  const [label, setLabel] = useState('');
  const [code, setCode] = useState('');
  const [codeBeruehrt, setCodeBeruehrt] = useState(false);
  const [icon, setIcon] = useState<string | null>(null);
  const anlegen = useWegAnlegen(art);
  const toast = useToast();

  const codeWert = codeBeruehrt ? code : codeVorschlag(label);

  function speichern(): void {
    anlegen.mutate(
      {
        code: codeWert,
        label: label.trim(),
        ...(sorte.mitIcon ? { icon } : {})
      },
      {
        onSuccess: (d) => {
          toast('success', `Angelegt: ${d.weg.label}.`);
          onFertig();
        },
        onError: (e) => toast('error', fehlerText(e))
      }
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-akzent bg-cream-warm p-3">
      <h3 className="text-sm text-ink">Neuer {sorte.einzahl}</h3>

      <label className="block text-xs text-ink-soft">
        Beschriftung
        <input
          type="text"
          value={label}
          maxLength={100}
          onChange={(e) => setLabel(e.target.value)}
          className={`mt-1 ${FELD_KLASSE}`}
        />
      </label>

      {sorte.mitIcon && <IconWahl wert={icon} onWahl={setIcon} />}

      <label className="block text-xs text-ink-soft">
        Schlüssel
        <input
          type="text"
          value={codeWert}
          maxLength={32}
          onChange={(e) => {
            setCodeBeruehrt(true);
            setCode(e.target.value.toLowerCase());
          }}
          className={`mt-1 ${FELD_KLASSE} font-mono`}
        />
        <span className="mt-1 block text-ink-faint">
          Kleinbuchstaben, Ziffern, Unterstrich. Wird einmal vergeben und lässt sich
          später nicht mehr ändern.
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          onClick={speichern}
          disabled={anlegen.isPending || label.trim() === '' || codeWert === ''}
        >
          {anlegen.isPending ? <Spinner /> : null}
          Anlegen
        </Button>
        <Button variant="ghost" onClick={onFertig}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}

/**
 * Aus der Beschriftung einen brauchbaren Schlüssel machen.
 *
 * Umlaute werden ausgeschrieben, nicht weggeworfen: aus „Bücherwagen"
 * wird `buecherwagen` und nicht `bcherwagen`.
 */
export function codeVorschlag(label: string): string {
  const klein = label
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  // Der Server verlangt einen Buchstaben vorn und mindestens zwei
  // Zeichen; was das nicht erfüllt, bleibt leer statt falsch.
  return /^[a-z][a-z0-9_]{1,31}$/.test(klein) ? klein : '';
}

/** Die Sinnbild-Wahl — nur für Bestellwege. */
function IconWahl({ wert, onWahl }: { wert: string | null; onWahl: (c: string | null) => void }) {
  return (
    <fieldset>
      <legend className="text-xs text-ink-soft">Sinnbild</legend>
      <div className="mt-1 flex flex-wrap gap-1">
        {BESTELLWEG_ICONS.map(({ code, name, Icon }) => {
          const gewaehlt = wert === code;
          return (
            <button
              key={code}
              type="button"
              onClick={() => onWahl(gewaehlt ? null : code)}
              aria-pressed={gewaehlt}
              aria-label={name}
              title={name}
              className={`inline-flex h-11 w-11 items-center justify-center rounded-md border ${
                gewaehlt
                  ? 'border-akzent bg-akzent-sanft text-akzent-stark'
                  : 'border-line text-ink-soft'
              }`}
            >
              <Icon aria-hidden className="h-5 w-5" />
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
